import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { ArrowLeft, Video, Trophy, Plus, Trash2, Edit2, Upload, Image, Film, Calendar, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAMS = [
  { value: '10u', label: '10U' },
  { value: '11u', label: '11U' },
  { value: '12u', label: '12U' },
  { value: '13u', label: '13U' },
  { value: '14u', label: '14U' },
  { value: '2026', label: '2026' },
  { value: '2027', label: '2027' },
  { value: '2028', label: '2028' },
  { value: '2029', label: '2029' },
  { value: '2030', label: '2030' },
];

const HIGH_SCHOOL_TEAMS = [
  { value: '2026', label: '2026' },
  { value: '2027', label: '2027' },
  { value: '2028', label: '2028' },
  { value: '2029', label: '2029' },
  { value: '2030', label: '2030' },
];

const YOUTH_TEAMS = [
  { value: '10u', label: '10U' },
  { value: '11u', label: '11U' },
  { value: '12u', label: '12U' },
  { value: '13u', label: '13U' },
  { value: '14u', label: '14U' },
];

const AdminTeamMedia = () => {
  const { isAuthenticated } = useAdmin();
  const [activeTab, setActiveTab] = useState('videos');
  const [teamVideos, setTeamVideos] = useState([]);
  const [standouts, setStandouts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Video form state
  const [videoForm, setVideoForm] = useState({ team: '', title: '', video_url: '' });
  const [videoUploading, setVideoUploading] = useState(false);
  
  // Standout form state
  const [standoutForm, setStandoutForm] = useState({ 
    team: '', title: '', media_url: '', media_type: 'image', description: '' 
  });
  const [standoutUploading, setStandoutUploading] = useState(false);
  const [editingStandout, setEditingStandout] = useState(null);
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({ team: '', schedule_url: '' });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [videosRes, standoutsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/admin/team-videos`),
        axios.get(`${API}/admin/team-standouts`),
        axios.get(`${API}/admin/team-schedules`)
      ]);
      setTeamVideos(videosRes.data || []);
      setStandouts(standoutsRes.data || []);
      setSchedules(schedulesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setVideoUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVideoForm(prev => ({ ...prev, video_url: response.data.url }));
      setMessage({ type: 'success', text: 'Video uploaded successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error uploading video' });
    } finally {
      setVideoUploading(false);
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    if (!videoForm.team || !videoForm.title || !videoForm.video_url) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    try {
      await axios.post(`${API}/admin/team-videos`, videoForm);
      setMessage({ type: 'success', text: `Video saved for ${videoForm.team.toUpperCase()} team` });
      setVideoForm({ team: '', title: '', video_url: '' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving video' });
    }
  };

  const handleDeleteVideo = async (team) => {
    if (!window.confirm(`Delete video for ${team.toUpperCase()} team?`)) return;

    try {
      await axios.delete(`${API}/admin/team-videos/${team}`);
      setMessage({ type: 'success', text: 'Video deleted' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting video' });
    }
  };

  const handleStandoutUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setStandoutUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const isVideo = file.type.startsWith('video/');
      const endpoint = isVideo ? `${API}/upload-video` : `${API}/upload-image`;
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStandoutForm(prev => ({ 
        ...prev, 
        media_url: response.data.url,
        media_type: isVideo ? 'video' : 'image'
      }));
      setMessage({ type: 'success', text: 'File uploaded successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error uploading file' });
    } finally {
      setStandoutUploading(false);
    }
  };

  const handleStandoutSubmit = async (e) => {
    e.preventDefault();
    if (!standoutForm.team || !standoutForm.title || !standoutForm.media_url) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      if (editingStandout) {
        await axios.put(`${API}/admin/team-standouts/${editingStandout}`, standoutForm);
        setMessage({ type: 'success', text: 'Standout updated' });
        setEditingStandout(null);
      } else {
        await axios.post(`${API}/admin/team-standouts`, standoutForm);
        setMessage({ type: 'success', text: 'Standout created' });
      }
      setStandoutForm({ team: '', title: '', media_url: '', media_type: 'image', description: '' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving standout' });
    }
  };

  const handleEditStandout = (standout) => {
    setStandoutForm({
      team: standout.team,
      title: standout.title,
      media_url: standout.media_url,
      media_type: standout.media_type,
      description: standout.description || ''
    });
    setEditingStandout(standout.id);
  };

  const handleDeleteStandout = async (id) => {
    if (!window.confirm('Delete this standout?')) return;

    try {
      await axios.delete(`${API}/admin/team-standouts/${id}`);
      setMessage({ type: 'success', text: 'Standout deleted' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting standout' });
    }
  };

  // Schedule handlers
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduleForm.team || !scheduleForm.schedule_url) {
      setMessage({ type: 'error', text: 'Please select a team and enter a Google Sheets URL' });
      return;
    }

    try {
      await axios.post(`${API}/admin/team-schedules`, scheduleForm);
      setMessage({ type: 'success', text: `Schedule saved for ${scheduleForm.team} team` });
      setScheduleForm({ team: '', schedule_url: '' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving schedule' });
    }
  };

  const handleDeleteSchedule = async (team) => {
    if (!window.confirm(`Delete schedule for ${team} team?`)) return;

    try {
      await axios.delete(`${API}/admin/team-schedules/${team}`);
      setMessage({ type: 'success', text: 'Schedule deleted' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting schedule' });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1>Access Denied</h1>
            <p>Please <Link to="/admin/login">login</Link> to access this page.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={20} /> Back to Dashboard
          </Link>
          <h1 className="display-large">Team Media</h1>
          <p className="body-large">Manage team videos and tournament standouts</p>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Message */}
          {message && (
            <div style={{
              padding: '12px 16px',
              background: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(220, 38, 38, 0.2)',
              border: `1px solid ${message.type === 'success' ? '#22c55e' : '#dc2626'}`,
              borderRadius: '8px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ color: message.type === 'success' ? '#22c55e' : '#ef4444' }}>{message.text}</span>
              <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1.25rem' }}>×</button>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('videos')}
              className={activeTab === 'videos' ? 'btn-primary' : 'btn-secondary'}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Video size={18} /> Team Videos
            </button>
            <button
              onClick={() => setActiveTab('standouts')}
              className={activeTab === 'standouts' ? 'btn-primary' : 'btn-secondary'}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Trophy size={18} /> Tournament Standouts
            </button>
            <button
              onClick={() => setActiveTab('schedules')}
              className={activeTab === 'schedules' ? 'btn-primary' : 'btn-secondary'}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Calendar size={18} /> Team Schedules
            </button>
          </div>

          {/* Team Videos Tab */}
          {activeTab === 'videos' && (
            <div>
              {/* Add/Edit Video Form */}
              <div className="network-card" style={{ marginBottom: '32px', padding: '24px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Plus size={20} /> Add Team Video
                </h3>
                <form onSubmit={handleVideoSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group">
                      <label>Team</label>
                      <select
                        value={videoForm.team}
                        onChange={(e) => setVideoForm(prev => ({ ...prev, team: e.target.value }))}
                        required
                      >
                        <option value="">Select Team</option>
                        {TEAMS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Video Title</label>
                      <input
                        type="text"
                        value={videoForm.title}
                        onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., 2024 Season Highlights"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Upload Video</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <label className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <Upload size={16} />
                        {videoUploading ? 'Uploading...' : 'Choose Video'}
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleVideoUpload}
                          style={{ display: 'none' }}
                          disabled={videoUploading}
                        />
                      </label>
                      {videoForm.video_url && (
                        <span style={{ color: '#22c55e', fontSize: '0.875rem' }}>Video uploaded ✓</span>
                      )}
                    </div>
                  </div>

                  <button type="submit" className="btn-primary" disabled={videoUploading}>
                    Save Team Video
                  </button>
                </form>
              </div>

              {/* Existing Videos List */}
              <h3 style={{ marginBottom: '16px' }}>Existing Team Videos</h3>
              {loading ? (
                <p>Loading...</p>
              ) : teamVideos.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No team videos added yet.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {teamVideos.map((video) => (
                    <div key={video.id || video.team} className="network-card" style={{ overflow: 'hidden' }}>
                      <video
                        src={video.video_url}
                        style={{ width: '100%', height: '150px', objectFit: 'cover', background: '#000' }}
                      />
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <span style={{
                              padding: '4px 8px',
                              background: 'rgba(212, 175, 55, 0.2)',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'var(--brand-primary)'
                            }}>
                              {video.team.toUpperCase()}
                            </span>
                            <h4 style={{ margin: '8px 0 0 0' }}>{video.title}</h4>
                          </div>
                          <button
                            onClick={() => handleDeleteVideo(video.team)}
                            className="btn-secondary"
                            style={{ padding: '6px', color: '#ef4444', borderColor: '#ef4444' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tournament Standouts Tab */}
          {activeTab === 'standouts' && (
            <div>
              {/* Add/Edit Standout Form */}
              <div className="network-card" style={{ marginBottom: '32px', padding: '24px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {editingStandout ? <Edit2 size={20} /> : <Plus size={20} />}
                  {editingStandout ? 'Edit Standout' : 'Add Tournament Standout'}
                </h3>
                <form onSubmit={handleStandoutSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group">
                      <label>Team</label>
                      <select
                        value={standoutForm.team}
                        onChange={(e) => setStandoutForm(prev => ({ ...prev, team: e.target.value }))}
                        required
                      >
                        <option value="">Select Team</option>
                        {TEAMS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={standoutForm.title}
                        onChange={(e) => setStandoutForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., MVP - Summer Tournament 2024"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Description (Optional)</label>
                    <textarea
                      value={standoutForm.description}
                      onChange={(e) => setStandoutForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the achievement..."
                      rows={2}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  </div>
                  
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Upload Image or Video</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <label className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <Upload size={16} />
                        {standoutUploading ? 'Uploading...' : 'Choose File'}
                        <input
                          type="file"
                          accept="image/*,video/*"
                          onChange={handleStandoutUpload}
                          style={{ display: 'none' }}
                          disabled={standoutUploading}
                        />
                      </label>
                      {standoutForm.media_url && (
                        <span style={{ color: '#22c55e', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {standoutForm.media_type === 'video' ? <Film size={14} /> : <Image size={14} />}
                          {standoutForm.media_type === 'video' ? 'Video' : 'Image'} uploaded ✓
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="submit" className="btn-primary" disabled={standoutUploading}>
                      {editingStandout ? 'Update Standout' : 'Add Standout'}
                    </button>
                    {editingStandout && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setEditingStandout(null);
                          setStandoutForm({ team: '', title: '', media_url: '', media_type: 'image', description: '' });
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Existing Standouts List */}
              <h3 style={{ marginBottom: '16px' }}>Existing Standouts</h3>
              {loading ? (
                <p>Loading...</p>
              ) : standouts.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No standouts added yet.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {standouts.map((standout) => (
                    <div key={standout.id} className="network-card" style={{ overflow: 'hidden' }}>
                      {standout.media_type === 'video' ? (
                        <video
                          src={standout.media_url}
                          style={{ width: '100%', height: '150px', objectFit: 'cover', background: '#000' }}
                        />
                      ) : (
                        <img
                          src={standout.media_url}
                          alt={standout.title}
                          style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                        />
                      )}
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                          <span style={{
                            padding: '4px 8px',
                            background: 'rgba(212, 175, 55, 0.2)',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'var(--brand-primary)'
                          }}>
                            {standout.team.toUpperCase()}
                          </span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => handleEditStandout(standout)}
                              className="btn-secondary"
                              style={{ padding: '6px' }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteStandout(standout.id)}
                              className="btn-secondary"
                              style={{ padding: '6px', color: '#ef4444', borderColor: '#ef4444' }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem' }}>{standout.title}</h4>
                        {standout.description && (
                          <p style={{ margin: 0, color: 'var(--text-light)', fontSize: '0.875rem' }}>
                            {standout.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Team Schedules Tab */}
          {activeTab === 'schedules' && (
            <div>
              {/* Add Schedule Form */}
              <div className="network-card" style={{ marginBottom: '32px', padding: '24px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Plus size={20} /> Set Team Schedule
                </h3>
                <form onSubmit={handleScheduleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group">
                      <label>Team</label>
                      <select
                        value={scheduleForm.team}
                        onChange={(e) => setScheduleForm(prev => ({ ...prev, team: e.target.value }))}
                        required
                      >
                        <option value="">Select Team</option>
                        <optgroup label="Youth Teams">
                          {YOUTH_TEAMS.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="High School Teams">
                          {HIGH_SCHOOL_TEAMS.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Google Sheets URL</label>
                      <input
                        type="url"
                        value={scheduleForm.schedule_url}
                        onChange={(e) => setScheduleForm(prev => ({ ...prev, schedule_url: e.target.value }))}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        required
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn-primary">
                    Save Schedule
                  </button>
                </form>
              </div>

              {/* Existing Schedules List */}
              <h3 style={{ marginBottom: '16px' }}>Existing Team Schedules</h3>
              {loading ? (
                <p>Loading...</p>
              ) : schedules.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No team schedules added yet.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {schedules.map((schedule) => (
                    <div key={schedule.id || schedule.team} className="network-card" style={{ padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <span style={{
                            padding: '6px 12px',
                            background: 'var(--brand-primary)',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: '#000'
                          }}>
                            {schedule.team} Team
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteSchedule(schedule.team)}
                          className="btn-secondary"
                          style={{ padding: '6px', color: '#ef4444', borderColor: '#ef4444' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div style={{ marginTop: '16px' }}>
                        <a
                          href={schedule.schedule_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: 'var(--brand-primary)',
                            textDecoration: 'none',
                            fontSize: '0.875rem'
                          }}
                        >
                          <ExternalLink size={14} />
                          View Google Sheet
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminTeamMedia;
